import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  DataTableDataSearchDto,
  DetailPageWithoutFilterDto,
  InfoIconDto,
  PageDto,
  SortColumnDto,
  TableColumnConfigDto,
  TableDataDto,
  TableDto,
  TableRowDto,
} from '@asset-metrix/portal-gateway-client-js';
import { isPureArray, WidgetProps } from '@asset-metrix/portal-ui';

import { downloadTableStaticFile } from '../../api/portalGateway/datatableStaticFileDownload';
import { NO_ROLE_ID } from '../../api/portalGatewayClient';
import { useStore } from '../../store';
import { actionWithAsyncNotification } from '../../utils/actionWithAsyncNotification';
import { buildDetailPageNavigationState } from '../../utils/detailPageLinkDisplay';
import { extractErrorMessage } from '../../utils/errorMessage';

import { convertToCustomizationData } from './convertToCustomizationData';
import { getDetailPageUrl } from './getDetailPageUrl';
import { useOriginHrefForDetailsPage } from './useOriginHrefForDetailsPage';
import { getId, widgetBulkAction } from './widgetBulkAction';
import {
  buildShareActionsWithInsertIndex,
  widgetGroupDataShareActions,
  widgetSelectedRowsShareActions,
  widgetShareAction,
} from './widgetShareAction';

export type BaseUseTableWidgetActionProps = {
  customFilterColumns: TableColumnConfigDto[];
  infoIcon?: InfoIconDto;
  isCustomizable: boolean;
  isColumnInitiallyHidden: boolean;
  isCustomized: boolean;
  onModalOpened?: (
    modalId: string,
    rowIds: string[],
    action: string,
    data: TableRowDto['data'][],
    documentId?: string
  ) => void;
  title: string;
  widgetId: string;
  closeFullscreen?: () => void;
  onBulkUpdateTableRowModalOpened?: (
    rowData: { rowId: string; data: TableRowDto['data'] }[]
  ) => void;
  onInitFundOperationModalOpened?: () => void;
  onCreateRowModalOpened?: () => void;
  onComparisonDetailPageModalOpened?: (
    selectedRows: TableDataDto['items']
  ) => void;
  onDataIngestionModalOpened?: () => void;
  onCreateInvestmentLimitModalOpened?: () => void;
  openCustomFilter: () => void;
  openCustomization: () => void;
  openDocUpload: () => void;
  openFullscreen?: () => void;
  openInfoIcon: () => void;
  openZipUpload: () => void;
  openDeleteConfirmation: (rowIds: string[], onApply: () => void) => void;
};

export const useTableWidgetActions = ({
  activeCustomFilterIds,
  config,
  currentSelectedRows,
  currentSortItems,
  customFilterColumns,
  infoIcon,
  isCustomizable,
  isColumnInitiallyHidden,
  isCustomized,
  pageFilter,
  pageFilterSelections,
  tableFilterSelections,
  workflowStateSelection,
  title,
  widgetId,
  closeFullscreen,
  onModalOpened,
  onBulkUpdateTableRowModalOpened,
  onInitFundOperationModalOpened,
  onCreateRowModalOpened,
  onComparisonDetailPageModalOpened,
  onDataIngestionModalOpened,
  onCreateInvestmentLimitModalOpened,
  openCustomFilter,
  openCustomization,
  openDocUpload,
  openFullscreen,
  openInfoIcon,
  openZipUpload,
  openDeleteConfirmation,
  customizedColumns,
  stakeAdjusted,
  groupByColumnId,
  isGroupedTable,
  isGroupOnlyMode,
  pageData,
}: BaseUseTableWidgetActionProps & {
  activeCustomFilterIds: string[];
  config: TableDto['config'];
  currentSelectedRows: TableDataDto['items'];
  currentSortItems?: SortColumnDto[];
  pageFilter?: DataTableDataSearchDto['selection'];
  pageFilterSelections: DataTableDataSearchDto['selection'];
  tableFilterSelections: DataTableDataSearchDto['selection'];
  workflowStateSelection: DataTableDataSearchDto['workflowStateSelection'];
  customizedColumns: TableColumnConfigDto[];
  stakeAdjusted?: boolean;
  groupByColumnId?: string;
  isGroupedTable: boolean;
  isGroupOnlyMode: boolean;
  pageData?: PageDto;
}) => {
  const { t } = useTranslation();
  const originHref = useOriginHrefForDetailsPage(pageData);
  const actions: WidgetProps['actions'] = [];
  const roleId = useStore((state) => state.activeUserRole?.id) || NO_ROLE_ID;
  const navigate = useNavigate();

  if (config.initFundOperationJsonSchema) {
    actions.push({
      icon: { name: 'add' },
      label: t('app.widget.action.label.initFoundOperation'),
      title: t('app.widget.action.label.initFoundOperation'),
      cb: async () => {
        onInitFundOperationModalOpened?.();
      },
    });
  }

  if (
    config.createRowConfig &&
    config.createRowConfig.type !== 'unrealizedInvestmentRow' &&
    config.createRowConfig.type !== 'dataIngestion' &&
    config.createRowConfig.type !== 'createInvestmentLimitRow'
  ) {
    const { buttonLabel } = config.createRowConfig;
    actions.push({
      icon: { name: 'add' },
      label: buttonLabel ?? t('app.widget.action.label.createRow'),
      title: buttonLabel ?? t('app.widget.action.label.createRow'),
      cb: async () => {
        onCreateRowModalOpened?.();
      },
    });
  }

  if (config.createRowConfig?.type === 'createInvestmentLimitRow') {
    const { buttonLabel } = config.createRowConfig;
    actions.push({
      icon: { name: 'add' },
      label: buttonLabel ?? t('app.widget.action.label.createInvestmentLimit'),
      title: buttonLabel ?? t('app.widget.action.label.createInvestmentLimit'),
      cb: async () => {
        onCreateInvestmentLimitModalOpened?.();
      },
    });
  }

  if (config.createRowConfig?.type === 'dataIngestion') {
    const { buttonLabel } = config.createRowConfig;
    actions.push({
      icon: { name: 'add' },
      label: buttonLabel ?? t('app.widget.action.label.dataIngestion'),
      title: buttonLabel ?? t('app.widget.action.label.dataIngestion'),
      cb: async () => {
        onDataIngestionModalOpened?.();
      },
    });
  }

  if (config.enableStaticFileDownload) {
    const label =
      config.staticFileDownloadButtonLabel ??
      t('app.widget.action.label.downloadStaticFile');
    actions.push({
      icon: { name: 'download' },
      label,
      title: label,
      cb: async () => {
        await actionWithAsyncNotification(
          () => downloadTableStaticFile(widgetId),
          {
            startMessage: t('app.notifications.staticFileDownload.loading'),
            successMessage: t('app.notifications.staticFileDownload.success'),
            errorMessage: (error) =>
              extractErrorMessage(
                error,
                t('app.notifications.staticFileDownload.error')
              ),
          }
        );
      },
    });
  }

  if (config.comparisonDetailPageConfig?.hasComparisonDetailPage) {
    const { comparisonDetailPages } = config.comparisonDetailPageConfig;
    if (comparisonDetailPages.length > 0) {
      const isCompareDisabled = currentSelectedRows.length < 2;
      actions.push({
        disabled: isCompareDisabled,
        icon: { name: 'compare_arrows' },
        label: t('app.widget.action.label.compareData'),
        title: isCompareDisabled
          ? t('app.widget.action.tooltip.compareData')
          : t('app.widget.action.label.compareData'),
        cb: () => {
          onComparisonDetailPageModalOpened?.(currentSelectedRows);
        },
      });
    }
  }

  if (config.aggregationDetailPageConfig) {
    const isAggregateDisabled = currentSelectedRows.length < 2;
    actions.push({
      disabled: isAggregateDisabled,
      icon: { name: 'join' },
      label: t('app.widget.action.label.aggregateData'),
      title: isAggregateDisabled
        ? t('app.widget.action.tooltip.aggregateData')
        : t('app.widget.action.label.aggregateData'),
      // Navigation deferred to PF-4891 — button is visible but has no handler yet.
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      cb: () => {},
    });
  }

  if (config.enableDocumentZipUpload) {
    actions.push({
      icon: { svgIcon: true, name: 'upload-zip' },
      title: t('app.upload.uploadZip'),
      cb: openZipUpload,
    });
  }

  if (config.uploadJsonSchema) {
    actions.push({
      icon: { name: 'file-move', svgIcon: true },
      title: t('app.upload.uploadDocuments'),
      cb: openDocUpload,
    });
  }

  if (config.updateJsonSchema) {
    actions.push({
      disabled:
        currentSelectedRows.length === 0 ||
        (config.maxBulkEditItemCount != null &&
          currentSelectedRows.length > config.maxBulkEditItemCount),
      icon: { name: 'edit' },
      title: t('app.widget.action.label.edit'),
      cb: async () => {
        const rowData = currentSelectedRows.map((row) => ({
          rowId: String(getId(row)),
          data: row,
          file_name: row.file_name,
        }));

        onBulkUpdateTableRowModalOpened?.(rowData);
      },
    });
  }

  if (customFilterColumns.length > 0) {
    actions.push({
      icon:
        activeCustomFilterIds.length > 0
          ? { name: 'filter-active', svgIcon: true }
          : { name: 'filter', svgIcon: true },
      title: t('app.widget.action.label.customFilter'),
      cb: openCustomFilter,
    });
  }

  if (isCustomizable) {
    actions.push({
      icon: isCustomized
        ? { name: 'settings-active', svgIcon: true }
        : isColumnInitiallyHidden
          ? { name: 'settings-invisible', svgIcon: true }
          : { name: 'settings', solid: true },
      title: t('app.widget.action.label.customization'),
      cb: openCustomization,
    });
  }

  // In group-only mode groupByColumnId is provided by the caller.
  // In a normal grouped table, derive it from the primary (level-0) grouping column.
  const effectiveGroupByColumnId =
    groupByColumnId ?? customizedColumns.find((col) => col.grouping === 0)?.id;

  const customization = convertToCustomizationData(
    customizedColumns,
    groupByColumnId
  );
  const shareActionKeys = new Set(
    config.shareActions?.map((sa) => sa.actionKey)
  );

  const canDownloadCsv =
    shareActionKeys.has('downloadCsv') ||
    shareActionKeys.has('downloadCsvUnfiltered');
  const canDownloadXlsx =
    shareActionKeys.has('downloadXlsx') ||
    shareActionKeys.has('downloadXlsxUnfiltered') ||
    shareActionKeys.has('downloadXlsxReadOnly');
  const readOnlyXlsx =
    !shareActionKeys.has('downloadXlsx') &&
    !shareActionKeys.has('downloadXlsxUnfiltered') &&
    shareActionKeys.has('downloadXlsxReadOnly');

  const rawActionRowIds = currentSelectedRows
    .map((row) => getId(row))
    .filter(
      (rowId): rowId is string | number =>
        typeof rowId === 'string' || typeof rowId === 'number'
    );

  const selectedActionRowIds: string[] | number[] = isPureArray(rawActionRowIds)
    ? rawActionRowIds
    : rawActionRowIds.map(String);

  const {
    actions: portalGatewayConfigShareActions,
    insertIndex: groupDataInsertIndex,
  } = buildShareActionsWithInsertIndex(config.shareActions ?? [], (sa) =>
    widgetShareAction(widgetId, title || config.title, sa, t, {
      currentSortItems,
      pageFilterSelections,
      tableFilterSelections,
      workflowStateSelection,
      customization,
      stakeAdjusted,
      groupByColumnId,
      onlyGroupRows: isGroupOnlyMode || undefined,
    })
  );

  const selectedRowsActions =
    config.enableRowSelection && selectedActionRowIds.length > 0
      ? widgetSelectedRowsShareActions(widgetId, title || config.title, t, {
          actionRowIds: selectedActionRowIds,
          canDownloadCsv,
          canDownloadXlsx,
          currentSortItems,
          customization,
          readOnlyXlsx,
          stakeAdjusted,
          groupByColumnId,
        })
      : [];

  const groupDataActions =
    isGroupedTable && !isGroupOnlyMode
      ? widgetGroupDataShareActions(widgetId, title || config.title, t, {
          canDownloadCsv,
          canDownloadXlsx,
          readOnlyXlsx,
          pageFilterSelections,
          tableFilterSelections,
          workflowStateSelection,
          currentSortItems,
          customization,
          stakeAdjusted,
          groupByColumnId: effectiveGroupByColumnId,
        })
      : [];

  const widgetShareActions = [
    ...selectedRowsActions,
    ...portalGatewayConfigShareActions.slice(0, groupDataInsertIndex),
    ...groupDataActions,
    ...portalGatewayConfigShareActions.slice(groupDataInsertIndex),
  ];

  if (widgetShareActions.length > 0) {
    actions.push({
      icon: { name: 'share' },
      title: t('app.widget.action.label.shareActions'),
      moreActions: widgetShareActions,
    });
  }

  if (infoIcon != null) {
    actions.push({
      icon: { name: 'info' },
      title: t('app.widget.action.label.additionalInformation'),
      cb: openInfoIcon,
    });
  }

  if (openFullscreen) {
    actions.push({
      icon: { name: 'open_in_full' },
      title: t('app.widget.action.label.openInFullscreen'),
      cb: openFullscreen,
    });
  }
  if (closeFullscreen) {
    actions.push({
      icon: { name: 'close' },
      title: t('app.widget.action.label.closeFullscreen'),
      cb: closeFullscreen,
    });
  }

  const openDetailPage = (
    detailPageConfig: DetailPageWithoutFilterDto,
    dataRows: Record<string, unknown>[],
    linkColumnKey?: string
  ) => {
    const href = getDetailPageUrl(
      roleId,
      { ...detailPageConfig, additionalFilterColumnKeys: [] },
      pageFilter,
      dataRows
    );

    navigate(href, {
      state: buildDetailPageNavigationState(
        originHref,
        detailPageConfig,
        linkColumnKey,
        dataRows
      ),
    });
  };

  const openDetailPageFromBulkAction = (
    detailPageConfig: DetailPageWithoutFilterDto,
    dataRows: Record<string, unknown>[]
  ) => {
    const linkColumnKey = config.columns.find((column) =>
      column.detailPageConfig?.some(
        (detailPage) => detailPage.id === detailPageConfig.id
      )
    )?.key;
    openDetailPage(detailPageConfig, dataRows, linkColumnKey);
  };

  const widgetBulkActions =
    config.bulkActions?.map((ba) =>
      widgetBulkAction(
        widgetId,
        title || config.title,
        ba,
        config.cacheKey,
        currentSelectedRows,
        pageFilterSelections,
        t,
        openDetailPageFromBulkAction,
        openDeleteConfirmation,
        onModalOpened
      )
    ) ?? [];

  if (widgetBulkActions.length > 0) {
    actions.push({
      icon: { name: 'more_vert' },
      title: t('app.widget.action.label.bulkActions'),
      moreActions: widgetBulkActions,
    });
  }

  return actions;
};
